import type { InfiniteData, Query, QueryClient } from "@tanstack/react-query";
import {
  getNotificationInboxItems,
  type NotificationInboxFilters,
  type NotificationInboxPage,
  type NotificationInboxParams,
} from "@/lib/data/requests/matters/matter-notification";
import { queryKeys } from "@/lib/data/static/query-keys";
import type { APIResponse } from "@/lib/types";

export const notificationInboxQueryKeyPrefix = [
  queryKeys.matters,
  "notification-inbox",
] as const;

export const notificationInboxFilters = (
  filters: NotificationInboxFilters = {},
) => ({
  unread: Boolean(filters.unread),
  tasks: Boolean(filters.tasks),
  notes: Boolean(filters.notes),
  texts: Boolean(filters.texts),
});

export const getNotificationInboxQueryKey = (
  filters: NotificationInboxFilters = {},
) =>
  [
    ...notificationInboxQueryKeyPrefix,
    "single-page",
    notificationInboxFilters(filters),
  ] as const;

export const getNotificationInboxInfiniteQueryKey = (
  filters: NotificationInboxFilters = {},
) =>
  [
    ...notificationInboxQueryKeyPrefix,
    "infinite",
    notificationInboxFilters(filters),
  ] as const;

// Single-page and infinite observers keep their own data shapes, but share HTTP
// work for the same filters/cursor. These entries are cleared with user data.
export const fetchNotificationInboxPage = (
  queryClient: QueryClient,
  params: NotificationInboxParams = {},
) => {
  const filters = notificationInboxFilters(params);
  const cursor = params.cursor || null;
  return queryClient.fetchQuery({
    queryKey: [...notificationInboxQueryKeyPrefix, "page", filters, cursor],
    queryFn: ({ signal }) =>
      getNotificationInboxItems(
        { ...filters, cursor: cursor ?? undefined },
        signal,
      ),
    staleTime: 5 * 60 * 1000,
    // Outer observers and the stream own retries; avoid multiplying attempts.
    retry: false,
  });
};

type InboxResponse = APIResponse<NotificationInboxPage>;
type Refresh = {
  pending: boolean;
  canonical: boolean;
  full: boolean;
  promise: Promise<InboxResponse | undefined>;
};

const refreshes = new WeakMap<QueryClient, Refresh>();
// Paging updates dataUpdatedAt, so retain the first deferred time independently.
const deferredHistory = new WeakMap<Query, number>();

// Keep an in-flight refresh intact. A change received after it starts needs a
// trailing cycle, so it cannot disappear behind an older response.
export const refreshNotificationInbox = (
  queryClient: QueryClient,
  canonical = false,
  preserveHistory = false,
): Promise<InboxResponse | undefined> => {
  const current = refreshes.get(queryClient);
  if (current) {
    current.pending = true;
    current.canonical ||= canonical;
    current.full ||= !preserveHistory;
    return current.promise;
  }

  const refresh: Refresh = {
    pending: true,
    canonical,
    full: !preserveHistory,
    promise: Promise.resolve(undefined),
  };
  refreshes.set(queryClient, refresh);
  refresh.promise = Promise.resolve().then(async () => {
    let snapshot: InboxResponse | undefined;
    try {
      do {
        refresh.pending = false;
        // A page already in flight may predate this change. Let it settle before
        // invalidating, so it cannot satisfy this cycle with an older snapshot.
        await Promise.allSettled(
          queryClient
            .getQueryCache()
            .findAll({ queryKey: notificationInboxQueryKeyPrefix })
            .filter((query) => query.state.fetchStatus === "fetching")
            .map((query) => query.promise),
        );
        await queryClient.invalidateQueries({
          queryKey: notificationInboxQueryKeyPrefix,
          refetchType: "none",
        });
        const reconciledHistory: Query[] = [];
        await queryClient.refetchQueries(
          {
            queryKey: notificationInboxQueryKeyPrefix,
            type: "active",
            predicate: (query) => {
              if (query.queryKey[2] !== "infinite") return true;
              const pages =
                (query.state.data as InfiniteData<InboxResponse> | undefined)
                  ?.pages.length ?? 0;
              const deferredAt = deferredHistory.get(query);
              if (
                refresh.full ||
                pages <= 1 ||
                (deferredAt !== undefined && deferredAt + 60_000 <= Date.now())
              ) {
                reconciledHistory.push(query);
                return true;
              }
              if (deferredAt === undefined)
                deferredHistory.set(query, Date.now());
              return false;
            },
          },
          { cancelRefetch: false, throwOnError: true },
        );
        reconciledHistory.forEach((query) => deferredHistory.delete(query));
        if (refresh.canonical) {
          snapshot = await fetchNotificationInboxPage(queryClient);
          queryClient.setQueryData(getNotificationInboxQueryKey(), snapshot);
        }
      } while (refreshes.get(queryClient)?.pending);
      return snapshot;
    } finally {
      refreshes.delete(queryClient);
    }
  });
  return refresh.promise;
};

// Restart from one authoritative head rather than replacing only page one and
// dropping a boundary row between the refreshed head and preserved history.
export const loadLatestNotificationInbox = async (
  queryClient: QueryClient,
  filters: NotificationInboxFilters,
) => {
  const queryKey = getNotificationInboxInfiniteQueryKey(filters);
  await queryClient.cancelQueries({ queryKey, exact: true });
  // Join the coordinator so a concurrent read/delete or newer live change
  // wins before the reset. The matching head observer shares this fresh page.
  await refreshNotificationInbox(queryClient, false, true);
  const head = await fetchNotificationInboxPage(queryClient, filters);
  // Another mounted list can start paging while the head loads. Cancel again
  // immediately before replacing the shared cache so it cannot restore history.
  await queryClient.cancelQueries({ queryKey, exact: true });
  queryClient.setQueryData<InfiniteData<InboxResponse, string | null>>(
    queryKey,
    {
      pages: [head],
      pageParams: [null],
    },
  );
  const query = queryClient.getQueryCache().find({ queryKey, exact: true });
  if (query) deferredHistory.delete(query);
  queryClient.setQueryData(getNotificationInboxQueryKey(filters), head);
};
